import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Loader2, RefreshCw, Download, Search, ShieldAlert, ChevronDown, ChevronUp,
  ArrowUpDown, Crown, TrendingDown,
} from "lucide-react";
import {
  fmt, cls, useConsulta, Panel, Err, Baneado, JUEGOS, JUEGO_LABEL, BAN_TIPO, SinClub,
} from "./ApiPPPokerConsultas";

const CASI_EN_EL_TOPE = 80;   // % del tope de pérdida a partir del cual avisamos

// La API corta los períodos con la hora de la ORGANIZACIÓN, no con la nuestra:
// `league/stats` no acepta `utc_offset` (lo resuelve por jerarquía superliga → liga → club),
// igual que la app. Para la unión 2006 eso es UTC-05:00, una hora atrás de Argentina.
const HORA_UNION = "UTC-05:00";

const FLAG = {
  evchop: "limit_evchop", jackpot: "limit_jackpot", luckydraw: "limit_luckydraw",
  colorgame: "limit_colorgame", russian_poker: "limit_russian_poker", crashgame: "limit_crashgame",
};
const SIGLA = {
  evchop: "EV", jackpot: "JP", luckydraw: "LD", colorgame: "CG",
  russian_poker: "RP", crashgame: "CR", others: "Otros",
};

/** Suma de TODOS los juegos: es el número que muestra la app. */
const totalDe = (c, periodo) => JUEGOS.reduce((a, g) => a + (c[`${g}_${periodo}_profit`] || 0), 0);
/** `others` siempre cuenta para el tope; el resto sólo si tiene su flag prendido. */
const cuentaParaTope = (c, g) => g === "others" || !!c[FLAG[g]];

/** % del tope de pérdida consumido (sólo si el club viene perdiendo y tiene tope). */
const usoDelTope = (c) =>
  c.loss_limit > 0 && c.current_profit < 0
    ? Math.min(100, (Math.abs(c.current_profit) / c.loss_limit) * 100)
    : null;

const Tarjeta = ({ label, valor, detalle, tono = "", testid, Icono }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-3">
    <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
      {Icono && <Icono className="w-3.5 h-3.5" />} {label}
    </p>
    <p className={`text-lg font-bold tabular-nums ${tono || "text-gray-900 dark:text-gray-100"}`} data-testid={testid}>
      {valor}
    </p>
    {detalle && <p className="text-xs text-gray-400 mt-0.5">{detalle}</p>}
  </div>
);

const BarraTope = ({ uso }) => {
  if (uso === null) return <span className="text-gray-400">sin tope</span>;
  const color = uso >= CASI_EN_EL_TOPE ? "bg-rose-500" : uso >= 50 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="flex items-center gap-2 justify-end">
      <div className="w-16 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${uso}%` }} />
      </div>
      <span className={`tabular-nums ${uso >= CASI_EN_EL_TOPE ? "text-rose-600 dark:text-rose-400 font-semibold" : ""}`}>
        {uso.toFixed(0)}%
      </span>
    </div>
  );
};

const Th = ({ campo, orden, setOrden, children, alinear = "left" }) => (
  <th className={`px-3 py-2 text-${alinear} whitespace-nowrap`}>
    <button onClick={() => setOrden({ campo, desc: orden.campo === campo ? !orden.desc : true })}
      data-testid={`union-orden-${campo}`}
      className={`inline-flex items-center gap-1 hover:text-violet-600 dark:hover:text-violet-400 ${
        orden.campo === campo ? "text-violet-600 dark:text-violet-400 font-semibold" : ""}`}>
      {children} <ArrowUpDown className="w-3 h-3 opacity-60" />
    </button>
  </th>
);

/**
 * Clubes de la unión: lo que devuelve el host (EVOLUTION) para los 29 clubes de la
 * liga 2006. Con el vendor de HALCÓN esto venía vacío — sólo el host ve la unión.
 */
export function TabClubesUnion({ clubId }) {
  const [data, loading, ejecutar, error] = useConsulta("league");
  const [busqueda, setBusqueda] = useState("");
  const [orden, setOrden] = useState({ campo: "current_profit", desc: false });
  const [periodo, setPeriodo] = useState("weekly");
  const [desglose, setDesglose] = useState(false);

  const traer = useCallback(() => { if (clubId) ejecutar({ club_id: clubId }); }, [clubId]); // eslint-disable-line
  useEffect(() => { traer(); }, [traer]);

  const info = data?.info || [];

  const resumen = useMemo(() => ({
    clubes: info.length,
    suspendidos: info.filter((c) => c.is_banned).length,
    conTope: info.filter((c) => c.loss_limit > 0).length,
    enRiesgo: info.filter((c) => (usoDelTope(c) ?? 0) >= CASI_EN_EL_TOPE).length,
    semana: info.reduce((a, c) => a + totalDe(c, "weekly"), 0),
    dia: info.reduce((a, c) => a + totalDe(c, "daily"), 0),
    tope: info.reduce((a, c) => a + (c.current_profit || 0), 0),
  }), [info]);

  const filas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    const lista = info.filter((c) =>
      !q || String(c.club_id).includes(q) || (c.club_name || "").toLowerCase().includes(q));
    const { campo, desc } = orden;
    const valor = (c) => (campo === "club_name" ? (c.club_name || "")
      : campo === "total" ? totalDe(c, periodo) : (c[campo] ?? 0));
    return [...lista].sort((a, b) => {
      const x = valor(a);
      const y = valor(b);
      if (typeof x === "string") return desc ? y.localeCompare(x) : x.localeCompare(y);
      return desc ? y - x : x - y;
    });
  }, [info, busqueda, orden, periodo]);

  const exportar = () => {
    const cols = ["club_id", "club_name", "is_banned", "limit_type", "current_profit",
      "loss_limit", "profit_limit", "period_limit",
      ...JUEGOS.map((g) => `${g}_${periodo}_profit`)];
    const num = (v) => (typeof v === "number" ? String(v).replace(".", ",") : v);
    const csv = [[`total_${periodo}`, ...cols].join(";")].concat(
      filas.map((c) => [num(totalDe(c, periodo)), ...cols.map((k) => num(c[k]))].join(";"))
    ).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: "text/csv;charset=utf-8" }));
    a.download = `union_${data?.league_id || "pppoker"}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  if (!clubId) return <SinClub />;

  return (
    <div className="space-y-4" data-testid="api-pppoker-union">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={traer} disabled={loading} data-testid="union-refrescar"
          className="px-4 py-1.5 text-sm font-semibold rounded-lg bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-50 flex items-center gap-2">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Actualizar
        </button>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2 top-2 text-gray-400" />
          <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar club o ID" data-testid="union-buscar"
            className="pl-7 pr-2 py-1.5 text-sm border rounded-lg dark:bg-gray-700 dark:border-gray-600 w-56" />
        </div>
        {info.length > 0 && (
          <>
            <button onClick={() => setDesglose((v) => !v)} data-testid="union-desglose"
              className="px-3 py-1.5 text-sm rounded-lg border dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-1">
              {desglose ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />} Desglose por juego
            </button>
            <button onClick={exportar} data-testid="union-exportar"
              className="px-3 py-1.5 text-sm rounded-lg border dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-1">
              <Download className="w-4 h-4" /> CSV
            </button>
          </>
        )}
        {data && (
          <span className="text-sm text-gray-500" data-testid="union-liga">
            liga <b>{data.league_id}</b> · {filas.length} de {info.length} club(es)
          </span>
        )}
      </div>

      <Err error={error} />

      {info.length === 1 && !loading && (
        <p className="text-xs text-gray-500">
          Sólo aparece un club: la unión la ve el <b>host</b>. Elegí EVOLUTION arriba (es el host de la
          liga 2006); con el vendor de un club común la API devuelve nada más que ese club.
        </p>
      )}

      {info.length > 1 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Tarjeta label="Clubes en la unión" valor={resumen.clubes} testid="union-total-clubes"
              detalle={`${resumen.conTope} con tope configurado`} />
            <Tarjeta label="Suspendidos" valor={resumen.suspendidos} Icono={ShieldAlert}
              tono={resumen.suspendidos ? "text-rose-600 dark:text-rose-400" : ""}
              testid="union-suspendidos" />
            <Tarjeta label="Casi en el tope" valor={resumen.enRiesgo} Icono={TrendingDown}
              tono={resumen.enRiesgo ? "text-amber-600 dark:text-amber-400" : ""}
              detalle={`≥ ${CASI_EN_EL_TOPE}% del tope de pérdida`} testid="union-en-riesgo" />
            <Tarjeta label="Total de la semana" valor={fmt(resumen.semana)} tono={cls(resumen.semana)}
              detalle={`todos los juegos · para el tope ${fmt(resumen.tope)}`} testid="union-resultado" />
            <Tarjeta label="Total de hoy" valor={fmt(resumen.dia)} tono={cls(resumen.dia)}
              detalle={`día de la unión (${HORA_UNION})`} testid="union-resultado-dia" />
          </div>

          <Panel titulo="Riesgo y resultado por club" testid="union-tabla"
            extra={
              <div className="flex items-center gap-1 text-xs">
                {["weekly", "daily"].map((p) => (
                  <button key={p} onClick={() => setPeriodo(p)} data-testid={`union-periodo-${p}`}
                    className={`px-2 py-1 rounded-md ${periodo === p
                      ? "bg-violet-600 text-white"
                      : "text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"}`}>
                    {p === "weekly" ? "Semanal" : "Diario"}
                  </button>
                ))}
              </div>
            }>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
                  <tr>
                    <Th campo="club_name" orden={orden} setOrden={setOrden}>Club</Th>
                    <th className="px-3 py-2 text-left">Estado</th>
                    <th className="px-3 py-2 text-left">Modo</th>
                    <Th campo="total" orden={orden} setOrden={setOrden} alinear="right">
                      Total {periodo === "weekly" ? "semanal" : "de hoy"}
                    </Th>
                    <Th campo="current_profit" orden={orden} setOrden={setOrden} alinear="right">Para el tope</Th>
                    <Th campo="loss_limit" orden={orden} setOrden={setOrden} alinear="right">Tope pérdida</Th>
                    <th className="px-3 py-2 text-right">Tope usado</th>
                    <th className="px-3 py-2 text-left whitespace-nowrap">Cuentan</th>
                    <Th campo="profit_limit" orden={orden} setOrden={setOrden} alinear="right">Tope ganancia</Th>
                    {desglose && JUEGOS.map((g) => (
                      <th key={g} className="px-3 py-2 text-right whitespace-nowrap">{JUEGO_LABEL[g]}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filas.map((c) => {
                    const uso = usoDelTope(c);
                    const propio = c.club_id === clubId;
                    return (
                      <tr key={c.club_id} data-testid={`union-fila-${c.club_id}`}
                        className={`border-t dark:border-gray-700 ${propio
                          ? "bg-violet-50/60 dark:bg-violet-900/10"
                          : "hover:bg-gray-50 dark:hover:bg-gray-700/40"}`}>
                        <td className="px-3 py-1.5 whitespace-nowrap">
                          {propio && <Crown className="w-3.5 h-3.5 inline text-violet-500 mr-1" />}
                          <span className="font-medium text-gray-900 dark:text-gray-100">{c.club_name}</span>
                          <span className="text-gray-400 font-mono ml-1">{c.club_id}</span>
                        </td>
                        <td className="px-3 py-1.5 whitespace-nowrap">
                          <Baneado v={c.is_banned} />
                          {/* ban_type 2 = "ya fue desbaneado": lo trae casi todo club, no aporta */}
                          {c.is_banned || [1, 3].includes(c.ban_type)
                            ? <span className="text-gray-400 ml-1">({BAN_TIPO[c.ban_type]})</span> : null}
                        </td>
                        <td className="px-3 py-1.5">{c.limit_type === 1 ? "diario" : "semanal"}</td>
                        <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${cls(totalDe(c, periodo))}`}>
                          {fmt(totalDe(c, periodo))}
                        </td>
                        <td className={`px-3 py-1.5 text-right tabular-nums ${cls(c.current_profit)}`}>
                          {fmt(c.current_profit)}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{fmt(c.loss_limit)}</td>
                        <td className="px-3 py-1.5 text-right"><BarraTope uso={uso} /></td>
                        <td className="px-3 py-1.5 whitespace-nowrap">
                          {JUEGOS.filter((g) => g !== "others").map((g) => (
                            <span key={g} title={JUEGO_LABEL[g]}
                              className={`inline-block px-1 mr-0.5 rounded text-[10px] font-mono ${
                                cuentaParaTope(c, g)
                                  ? "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300"
                                  : "bg-gray-100 text-gray-400 dark:bg-gray-700/40 dark:text-gray-500 line-through"}`}>
                              {SIGLA[g]}
                            </span>
                          ))}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{fmt(c.profit_limit)}</td>
                        {desglose && JUEGOS.map((g) => (
                          <td key={g} className={`px-3 py-1.5 text-right tabular-nums ${cls(c[`${g}_${periodo}_profit`])}`}>
                            {fmt(c[`${g}_${periodo}_profit`])}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-3 py-2 text-xs text-gray-500 border-t dark:border-gray-700 space-y-1">
              <p>
                <b>«Total»</b> suma los 7 tipos de juego: es el número que muestra la app.
                <b> «Para el tope»</b> es el que PPPoker mira para suspender y suma sólo los juegos
                marcados en «Cuentan» (más «Otros», que siempre entra). En casi todos los clubes el
                <b> Jackpot está afuera</b>, así que los dos números no coinciden y esa diferencia es
                justamente el jackpot.
              </p>
              <p>
                ⏱ Los períodos se cortan con la <b>hora de la unión ({HORA_UNION})</b>, no con la de
                Argentina: el día arranca a las <b>02:00 ART</b> y la semana el lunes a esa hora. Este
                endpoint no acepta zona horaria — usa la de la organización, igual que la app.
              </p>
              <p>
                «Otros» agrupa NLH, PLO, OFC, MTT, SNG, SpinUp, Pusoy, Teen Patti, Tongits, Seka y
                Mataases. Blackjack no tiene categoría propia en la API. Todo en PP chips.
              </p>
            </div>
          </Panel>
        </>
      )}

      {loading && info.length === 0 && (
        <div className="py-16 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      )}
    </div>
  );
}

export default TabClubesUnion;
